import clsx from 'clsx';
import React from 'react';
import Tippy, { TippyProps } from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';
import 'tippy.js/animations/scale-subtle.css';

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';

export interface TooltipProps extends Omit<TippyProps, 'content' | 'children' | 'onShow' | 'onHide'> {
  message: React.ReactNode;
  children: React.ReactElement;
  bgColor?: string;
  textColor?: string;
  tooltipClassName?: string;
  containerClassName?: string;
  maxWidth?: number | string;
  showArrow?: boolean;
  onShow?: () => void;
  onHide?: () => void;
}

const Tooltip: React.FC<TooltipProps> = ({
  message,
  children,
  bgColor = 'bg-gray-800', // Default dark background
  textColor = 'text-white', // Default white text
  tooltipClassName = '',
  containerClassName = '',
  maxWidth = 300,
  showArrow = true,
  onShow,
  onHide,
  placement = 'top',
  delay = [100, 0], // [show, hide] in ms
  duration = [200, 150], // [show, hide] in ms
  offset = [0, 10], // [skid, distance]
  interactive = false,
  ...rest
}) => {
  // Default Tippy props
  const tippyProps: TippyProps = {
    content: message,
    placement,
    delay,
    duration,
    offset,
    interactive,
    arrow: showArrow,
    theme: 'light',
    animation: 'scale-subtle',
    appendTo: () => document.body,
    onShow,
    onHide,
    ...rest,
  };

  // Apply custom styling
  const tooltipStyle = {
    '--tippy-bg-color': bgColor,
    '--tippy-text-color': textColor,
    '--tippy-max-width': typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
  } as React.CSSProperties;

  return (
    <div className={clsx('inline-block', containerClassName)} style={tooltipStyle}>
      <Tippy
        {...tippyProps}
        content={
          <div className={clsx('px-3 py-2 text-sm', tooltipClassName)}>
            {message}
          </div>
        }
      >
        {React.cloneElement(children, {
          className: clsx('cursor-pointer', children.props.className),
        })}
      </Tippy>
    </div>
  );
};

export default Tooltip;
